import type { ComponentProps, ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Heading, Text } from './typography'
export function Page({
  className,
  width = 'content',
  ...props
}: ComponentProps<'section'> & { width?: 'full' | 'content' | 'form' }) {
  return (
    <section
      {...props}
      data-slot="page"
      className={cn(
        'flex w-full min-w-0 flex-col',
        width === 'content' && 'mx-auto max-w-6xl',
        width === 'form' && 'max-w-3xl',
        className,
      )}
    />
  )
}
export function PageHeader({
  actions,
  className,
  description,
  eyebrow,
  heading,
  illustration,
  metadata,
  headingLevel = 'h1',
  size = 'lg',
  ...props
}: Omit<ComponentProps<'header'>, 'children'> & {
  actions?: ReactNode
  description?: ReactNode
  eyebrow?: ReactNode
  heading: ReactNode
  illustration?: ReactNode
  metadata?: ReactNode
  headingLevel?: 'h1' | 'h2' | 'h3'
  size?: ComponentProps<typeof Heading>['size']
}) {
  return (
    <header
      {...props}
      data-slot="page-header"
      className={cn(
        'flex shrink-0 flex-wrap items-end justify-between gap-4 border-b border-border pb-6',
        className,
      )}
    >
      <div className="min-w-0 space-y-2">
        {eyebrow ? (
          <Text variant="eyebrow" tone="accent">
            {eyebrow}
          </Text>
        ) : null}
        <Heading as={headingLevel} size={size}>
          {heading}
        </Heading>
        {description ? <Text tone="muted">{description}</Text> : null}
        {metadata}
      </div>
      {illustration ? (
        <div aria-hidden="true" className="hidden shrink-0 self-center sm:block">
          {illustration}
        </div>
      ) : null}
      {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
    </header>
  )
}
export function PageBody({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      data-slot="page-body"
      className={cn('flex min-w-0 flex-1 flex-col pt-6 pb-12 sm:pb-16', className)}
    />
  )
}
